import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { clientConfigService } from "../../utils/services/clientConfigService";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import CustomTypography from "../../globalStyles/fonts";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import AddConfigModal from "../../components/AddConfigModal";
import LostSalesEventsSettings from "../../components/lost-sales-events-settings";
import AnalyticsWarehousesSetting from "../../components/analytics-warehouses-setting";
import ForecastSettingsDialog from "../../components/forecast-policy/ForecastSettingsDialog";
import DashboardSettings from "../../components/dashboard-settings";

// The backend's DEFAULT_CLIENT_CONFIG array (control-tower's
// default-client-config.class.js) is a flat, declaration-order list mixing
// order, purchasing, and forecast/analytics settings together — this groups
// them into the themed sections below purely for display. A config key the
// backend adds that isn't listed in any group here still shows up, under
// "Other Settings" at the end, so a future addition never silently
// disappears from the page.
const CONFIG_GROUPS = [
  {
    title: "Order Processing & Shipping",
    description:
      "Order numbering, shipment label fields, and manifest/shipment timing.",
    keys: [
      "order_process_delay",
      "order_id_prefix",
      "additional_col_shipments",
      "manifested_not_in_transit_delay_time",
    ],
  },
  {
    title: "Purchasing & Suppliers",
    description:
      "Purchase order numbering and how long suppliers get to confirm and dispatch.",
    keys: [
      "purchase_order_prefix",
      "master_purchase_order_prefix",
      "inbound_delay_time",
      "supplier_confirmation_delay",
      "supplier_dispatch_delay",
    ],
  },
  {
    title: "Forecast & Analytics",
    description:
      "How forecasts are calculated and which warehouses/calendar analytics reports use.",
    keys: [
      "forecast_lead_time_days",
      "sales_analysis_calendar_mode",
      "analytics_warehouse_ids",
    ],
  },
];

const ClientConfig = () => {
  const [clientConfigs, setClientConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState({});
  const [showAddNewModal, setShowAddNewModal] = useState(false);
  const client_id = localStorage.getItem("client_id");
  const [defaultClientConfigs, setDefaultClientConfigs] = useState([]);
  const [forecastSettingsOpen, setForecastSettingsOpen] = useState(false);
  // Which config section tab is showing. Only that one section's fields are
  // rendered at a time, instead of every section stacked down the page.
  const [activeTab, setActiveTab] = useState(0);

  const fetchClientConfig = async () => {
    try {
      const response = await clientConfigService.fetch(client_id);
      setClientConfigs(response.data || []);
    } catch (error) {
      console.error("Fetch failed:", error);
      setClientConfigs([]);
    }
  };

  useEffect(() => {
    fetchDefaultClientConfigs();
    fetchClientConfig();
  }, []);

  const fetchDefaultClientConfigs = async () => {
    try {
      const response = await clientConfigService.defaultConfigs();
      setDefaultClientConfigs(response || []);
    } catch (error) {
      console.error("Fetch default configs failed:", error);
      setDefaultClientConfigs([]);
    }
  };

  const handleKeyChange = (index, value) => {
    setSelectedConfig({
      ...selectedConfig,
      value: selectedConfig.value.map((x, i) => (i === index ? value : x)),
    });
  };

  const addKeyField = () => {
    setSelectedConfig({
      ...selectedConfig,
      value: [...selectedConfig.value, ""],
    });
  };

  const removeKeyField = (index) => {
    setSelectedConfig({
      ...selectedConfig,
      value: selectedConfig.value.filter((x, i) => i !== index),
    });
  };

  const onClose = () => {
    setEditingConfig(null);
    setSelectedConfig({});
    fetchClientConfig();
  };

  const updateClientConfig = async (data) => {
    try {
      const payload = {
        client_id,
        ...data,
      };

      if (selectedConfig._id) {
        await clientConfigService.edit(selectedConfig._id, payload);
      } else {
        await clientConfigService.create(payload);
      }

      toast.success("Client config updated successfully!");
      fetchClientConfig();
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    }
  };

  const handleSaveConfig = async (configKey) => {
    const config = defaultClientConfigs.find((c) => c.name === configKey);

    if (config?.type === "array") {
      const keysArray = selectedConfig.value.filter(
        (key) => key?.trim?.() !== ""
      );
      await updateClientConfig({
        key: configKey,
        value: keysArray,
      });
    } else if (config?.type === "number") {
      await updateClientConfig({
        key: configKey,
        value: Number(selectedConfig.value),
      });
    } else {
      await updateClientConfig({
        key: configKey,
        value: selectedConfig.value,
      });
    }

    setEditingConfig(null);
  };

  const handleAddNewConfig = async (configData) => {
    await updateClientConfig(configData);
  };

  const startEditing = (configKey) => {
    const existing = clientConfigs.find((x) => x.key === configKey);
    const defaultConfig = defaultClientConfigs.find(
      (c) => c.name === configKey
    );

    setSelectedConfig(
      existing || {
        key: configKey,
        value: defaultConfig?.defaultValue ?? "",
      }
    );
    setEditingConfig(configKey);
  };

  const getConfigValue = (configKey) => {
    const config = clientConfigs.find((x) => x.key === configKey);
    const defaultConfig = defaultClientConfigs.find(
      (c) => c.name === configKey
    );

    if (!config) {
      if (defaultConfig?.type === "select") {
        const option = defaultConfig.options?.find(
          (o) => o.value === defaultConfig.defaultValue
        );
        return option?.label ?? defaultConfig.defaultValue ?? "Not set";
      }
      return defaultConfig?.defaultValue ?? "Not set";
    }

    if (defaultConfig?.type === "array") {
      return config.value?.length ? config.value.join(", ") : "None";
    }

    if (defaultConfig?.type === "select") {
      const option = defaultConfig.options?.find(
        (o) => o.value === config.value
      );
      return option?.label ?? config.value ?? "Not set";
    }

    return config.value ?? "Not set";
  };

  const renderConfigField = (config) => {
    // Warehouse-list settings get a dedicated picker (names + codes, search,
    // paging) instead of the generic value editor.
    if (config.type === "warehouses") {
      return (
        <AnalyticsWarehousesSetting
          key={config.name}
          config={config}
          clientId={client_id}
        />
      );
    }
    const isEditing = editingConfig === config.name;
    const configKey = config.name;

    return (
      <Box
        key={configKey}
        sx={{
          borderRadius: 2,
          padding: 3,
          backgroundColor: "var(--bg-paper)",
          width: "100%",
          border: "1px solid var(--border-color)",
          position: "relative",
          mb: 2,
        }}
      >
        {!isEditing && (
          <IconButton
            onClick={() => startEditing(configKey)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <DriveFileRenameOutlineIcon sx={{ color: "#FF5733" }} />
          </IconButton>
        )}

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <Typography
              sx={{
                fontFamily: "poppins",
                fontSize: 14,
                fontWeight: 500,
                textAlign: "left",
                color: "var(--text-tertiary)",
              }}
            >
              {config.label}
            </Typography>
          </Grid>

          <Grid item xs={12} md={7}>
            {isEditing ? (
              <>
                {config.type === "array" ? (
                  <>
                    {selectedConfig.value?.map((key, index) => (
                      <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        key={index}
                        sx={{ mb: 1 }}
                      >
                        <Grid item xs={10}>
                          <TextField
                            fullWidth
                            value={key}
                            placeholder={`Value ${index + 1}`}
                            onChange={(e) =>
                              handleKeyChange(index, e.target.value)
                            }
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton onClick={() => removeKeyField(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        onClick={addKeyField}
                        sx={{
                          textTransform: "none",
                          fontSize: 13,
                          fontFamily: "poppins",
                          mt: 1,
                        }}
                      >
                        + Add more
                      </Button>
                    </Box>
                  </>
                ) : config.type === "select" ? (
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedConfig.value ?? config.defaultValue ?? ""}
                      onChange={(e) =>
                        setSelectedConfig({
                          ...selectedConfig,
                          value: e.target.value,
                        })
                      }
                    >
                      {(config.options || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    type={config.type === "number" ? "number" : "text"}
                    value={selectedConfig.value}
                    onChange={(e) =>
                      setSelectedConfig({
                        ...selectedConfig,
                        value: e.target.value,
                      })
                    }
                    size="small"
                  />
                )}

                <Box
                  sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                      mr: 2,
                      color: "red",
                      borderColor: "red",
                      fontFamily: "poppins",
                      fontSize: 14,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleSaveConfig(configKey)}
                    sx={{ fontFamily: "poppins", fontSize: 14 }}
                  >
                    Save
                  </Button>
                </Box>
              </>
            ) : (
              <Typography
                sx={{
                  fontFamily: "poppins",
                  fontSize: 14,
                  fontWeight: 400,
                  textAlign: "start",
                  color: "var(--text-secondary)",
                  wordBreak: "break-word",
                }}
              >
                {getConfigValue(configKey)}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Get custom configs that are not in the default list
  const customConfigs = clientConfigs.filter(
    (config) => !defaultClientConfigs.find((dc) => dc.name === config.key)
  );

  // One tab per themed group (only once it actually has a matching config —
  // keeps an empty group from showing as a dead tab), plus a trailing
  // "Other Settings" tab for any default config the backend added without
  // updating CONFIG_GROUPS above, so a future addition never silently
  // disappears from the page instead of just missing its own tab.
  const configSections = useMemo(() => {
    const groups = CONFIG_GROUPS.map((group) => ({
      title: group.title,
      description: group.description,
      isForecastGroup: group.title === "Forecast & Analytics",
      configs: group.keys
        .map((key) => defaultClientConfigs.find((c) => c.name === key))
        .filter(Boolean),
    })).filter((group) => group.configs.length);

    const groupedKeys = new Set(CONFIG_GROUPS.flatMap((g) => g.keys));
    const ungrouped = defaultClientConfigs.filter(
      (c) => !groupedKeys.has(c.name)
    );
    if (ungrouped.length) {
      groups.push({
        title: "Other Settings",
        description: "",
        isForecastGroup: false,
        configs: ungrouped,
      });
    }
    return groups;
  }, [defaultClientConfigs]);

  const activeSection =
    configSections[Math.min(activeTab, configSections.length - 1)];

  return (
    <Box
      sx={{
        width: "100%",
        height: "90vh",
        overflow: "auto",
        "&::-webkit-scrollbar": {
          width: 8,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: 4,
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#555",
        },
        // p: 3,
        backgroundColor: "var(--bg-primary)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          mb: 3,
          position: "sticky",
          top: 0,
          backgroundColor: "var(--bg-primary)",
          zIndex: 1,
          py: 2,
        }}
      >
        <CustomTypography
          sx={{
            textAlign: "start",
            fontWeight: 600,
            fontSize: 24,
            color: "var(--text-tertiary)",
          }}
        >
          Configuration Settings
        </CustomTypography>
        {/* <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddNewModal(true)}
          sx={{
            fontFamily: "poppins",
            fontSize: 14,
            textTransform: "none",
            backgroundColor: "#007DFF",
            "&:hover": {
              backgroundColor: "#0056CC",
            },
          }}
        >
          Config
        </Button> */}
      </Box>

      <Tabs
        value={Math.min(activeTab, Math.max(configSections.length - 1, 0))}
        onChange={(e, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
      >
        {configSections.map((section) => (
          <Tab
            key={section.title}
            label={section.title}
            sx={{ textTransform: "none", fontFamily: "poppins", fontWeight: 600 }}
          />
        ))}
      </Tabs>

      {activeSection && (
        <Box sx={{ mb: 4 }}>
          {activeSection.description && (
            <Typography
              sx={{
                fontFamily: "poppins",
                fontSize: 13,
                color: "text.secondary",
                mb: 2,
              }}
            >
              {activeSection.description}
            </Typography>
          )}
          {activeSection.configs.map((config) => renderConfigField(config))}

          {/* Lost Sales Events + Forecast Settings live in this tab too —
              both shape how this client's forecasts are produced, same as
              forecast_lead_time_days/sales_analysis_calendar_mode above.
              Promo days entered here feed the forecast, so this order
              (events, then the dialog that references them) is deliberate. */}
          {activeSection.isForecastGroup && <DashboardSettings clientId={client_id} />}
          {activeSection.isForecastGroup && <LostSalesEventsSettings />}
          {activeSection.isForecastGroup && client_id && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 1,
                }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontFamily: "poppins", fontSize: 18, fontWeight: 600 }}>
                    Forecast Settings
                  </Typography>
                  <Typography sx={{ fontFamily: "poppins", fontSize: 13, color: "text.secondary" }}>
                    Choose how your forecasts are made: how they're judged, how carefully methods are tested, which
                    methods may be used, and the holiday calendar. Promo days come from Lost Sales Events above.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => setForecastSettingsOpen(true)}
                  sx={{ textTransform: "none", fontFamily: "poppins", flexShrink: 0 }}
                >
                  Open settings
                </Button>
              </Box>
              <ForecastSettingsDialog
                open={forecastSettingsOpen}
                onClose={() => setForecastSettingsOpen(false)}
                clientId={client_id}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Custom Configurations */}
      {/* {customConfigs.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontFamily: "poppins",
              fontSize: 18,
              fontWeight: 600,
              mb: 2,
              color: "var(--text-tertiary)",
              alignContent: "start",
              display: "flex",
            }}
          >
            Custom Configurations
          </Typography>
          {customConfigs.map((config) => (
            <Box
              key={config.key}
              sx={{
                borderRadius: 2,
                padding: 3,
                backgroundColor: "var(--bg-paper)",
                width: "100%",
                border: "1px solid var(--border-color)",
                position: "relative",
                mb: 2,
              }}
            >
              <IconButton
                onClick={() => startEditing(config.key)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <DriveFileRenameOutlineIcon sx={{ color: "#FF5733" }} />
              </IconButton>

              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={5}>
                  <Typography
                    sx={{
                      fontFamily: "poppins",
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: "left",
                      color: "var(--text-tertiary)",
                    }}
                  >
                    {config.label || config.key}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Typography
                    sx={{
                      fontFamily: "poppins",
                      fontSize: 14,
                      fontWeight: 400,
                      textAlign: "start",
                      color: "var(--text-secondary)",
                      wordBreak: "break-word",
                    }}
                  >
                    {Array.isArray(config.value)
                      ? config.value.join(", ")
                      : config.value}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      )} */}

      {/* Add Configuration Modal */}
      <AddConfigModal
        open={showAddNewModal}
        onClose={() => setShowAddNewModal(false)}
        onSave={handleAddNewConfig}
        existingConfigs={clientConfigs}
        defaultConfigs={defaultClientConfigs}
      />
    </Box>
  );
};

export default ClientConfig;
